import { useEffect, useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { usePageTitle } from "@/hooks/usePageTitle";
import {
  defaultMetricsDateRange,
  formatMetricsRangeLabel,
  isValidMetricsDateRange,
  metricsPresetRange,
  type MetricsDateRange,
} from "@/lib/api/admin-metrics-date";
import {
  formatDurationSeconds,
  getAdminMetrics,
  mergeDailySeries,
  type AdminMetricsResponse,
} from "@/lib/api/admin-metrics";
import { AdminShell } from "@/pages/admin/AdminShell";

type LoadState =
  | { kind: "loading" }
  | { kind: "ready"; data: AdminMetricsResponse }
  | { kind: "error"; message: string };

const METRICS_PRESETS = [
  { label: "Last 7 days", days: 7 },
  { label: "Last 30 days", days: 30 },
  { label: "Last 90 days", days: 90 },
] as const;

const CHART_TICK = { fontSize: 12, fill: "var(--admin-chart-tick)" };
const CHART_TOOLTIP_STYLE = {
  background: "var(--card)",
  border: "1px solid var(--border)",
  color: "var(--t1)",
};

function formatDayLabel(day: string): string {
  const date = new Date(`${day}T00:00:00`);
  if (Number.isNaN(date.getTime())) {
    return day;
  }
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function SummaryCard({
  label,
  value,
  hint,
  tone,
  testId,
}: {
  label: string;
  value: string;
  hint?: string;
  tone?: "success" | "danger" | "warn";
  testId?: string;
}) {
  return (
    <div
      className={`sum-card metrics-card${tone ? ` metrics-card--${tone}` : ""}`}
      data-testid={testId}
    >
      <div className="sum-lbl">{label}</div>
      <div className="sum-val">{value}</div>
      {hint ? <div className="metrics-card-hint">{hint}</div> : null}
    </div>
  );
}

export default function AdminMetricsPage() {
  usePageTitle("Metrics — API Admin Panel");
  const initialRange = defaultMetricsDateRange();
  const [draftRange, setDraftRange] = useState<MetricsDateRange>(initialRange);
  const [appliedRange, setAppliedRange] = useState<MetricsDateRange>(initialRange);
  const [rangeError, setRangeError] = useState<string | null>(null);
  const [loadState, setLoadState] = useState<LoadState>({ kind: "loading" });

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoadState({ kind: "loading" });
      try {
        const data = await getAdminMetrics(appliedRange);
        if (!cancelled) {
          setLoadState({ kind: "ready", data });
        }
      } catch {
        if (!cancelled) {
          setLoadState({
            kind: "error",
            message: "Could not load metrics. Try again.",
          });
        }
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [appliedRange]);

  const chartData = useMemo(() => {
    if (loadState.kind !== "ready") {
      return [];
    }
    return mergeDailySeries(
      loadState.data.time_series.registrations_by_day,
      loadState.data.time_series.requests_by_day,
      loadState.data.time_series.emails_by_day,
    ).map((point) => ({
      ...point,
      label: formatDayLabel(point.day),
    }));
  }, [loadState]);

  const summary =
    loadState.kind === "ready" ? loadState.data.summary : null;
  const activeRange =
    loadState.kind === "ready" ? loadState.data.range : appliedRange;
  const rangeLabel = formatMetricsRangeLabel(activeRange.from, activeRange.to);

  function applyRange(next: MetricsDateRange) {
    if (!isValidMetricsDateRange(next.from, next.to)) {
      setRangeError("Start date must be on or before end date.");
      return;
    }
    setRangeError(null);
    setDraftRange(next);
    setAppliedRange(next);
  }

  function applyPreset(days: number) {
    applyRange(metricsPresetRange(days));
  }

  function handleApplyDates() {
    applyRange(draftRange);
  }

  return (
    <AdminShell
      activeNav="metrics"
      title="Metrics"
      description={`Portal adoption and request handling KPIs for ${rangeLabel}.`}
      testId="admin-metrics-page"
    >
      <div className="toolbar metrics-toolbar" data-testid="admin-metrics-toolbar">
        <div className="metrics-date-field">
          <label htmlFor="admin-metrics-from">From</label>
          <input
            id="admin-metrics-from"
            type="date"
            value={draftRange.from}
            onChange={(event) =>
              setDraftRange((prev) => ({ ...prev, from: event.target.value }))
            }
            data-testid="admin-metrics-from"
          />
        </div>
        <div className="metrics-date-field">
          <label htmlFor="admin-metrics-to">To</label>
          <input
            id="admin-metrics-to"
            type="date"
            value={draftRange.to}
            onChange={(event) =>
              setDraftRange((prev) => ({ ...prev, to: event.target.value }))
            }
            data-testid="admin-metrics-to"
          />
        </div>
        <button
          type="button"
          className="filter-btn"
          onClick={handleApplyDates}
          data-testid="admin-metrics-apply-range"
        >
          Apply
        </button>
        {METRICS_PRESETS.map((preset) => {
          const presetRange = metricsPresetRange(preset.days);
          const isActive =
            appliedRange.from === presetRange.from &&
            appliedRange.to === presetRange.to;
          return (
            <button
              key={preset.days}
              type="button"
              className={`filter-btn${isActive ? " on" : ""}`}
              aria-pressed={isActive}
              data-testid={`admin-metrics-preset-${preset.days}`}
              onClick={() => applyPreset(preset.days)}
            >
              {preset.label}
            </button>
          );
        })}
      </div>

      {rangeError ? (
        <div className="state-box error" data-testid="admin-metrics-range-error">
          {rangeError}
        </div>
      ) : null}

      {loadState.kind === "loading" ? (
        <p className="state-msg" data-testid="admin-metrics-loading">
          Loading metrics…
        </p>
      ) : null}

      {loadState.kind === "error" ? (
        <div className="state-box error" data-testid="admin-metrics-error">
          {loadState.message}
        </div>
      ) : null}

      {summary ? (
        <>
          <div className="sum-row metrics-sum-row" data-testid="admin-metrics-summary">
            <SummaryCard
              label="Registered users"
              value={summary.registered_users.toLocaleString()}
              testId="admin-metrics-registered-users"
            />
            <SummaryCard
              label="Access requests"
              value={summary.access_requests.toLocaleString()}
              testId="admin-metrics-access-requests"
            />
            <SummaryCard
              label="Emails sent"
              value={summary.emails_sent.toLocaleString()}
              testId="admin-metrics-emails-sent"
            />
            <SummaryCard
              label="Avg time to complete"
              value={formatDurationSeconds(summary.avg_time_to_complete_seconds)}
              hint="Submission to decision"
              testId="admin-metrics-avg-time"
            />
            <SummaryCard
              label="API keys issued"
              value={summary.api_keys_approved.toLocaleString()}
              tone="success"
              testId="admin-metrics-api-keys"
            />
            <SummaryCard
              label="Denied"
              value={summary.denied.toLocaleString()}
              tone="danger"
              testId="admin-metrics-denied"
            />
          </div>

          <div className="metrics-charts">
            <section className="metrics-chart-card" aria-label="Daily activity">
              <h2>Daily activity</h2>
              <p className="metrics-chart-copy">
                Registrations, access requests, and internal notification emails
                per day for {rangeLabel}.
              </p>
              <div className="metrics-chart-wrap" data-testid="admin-metrics-activity-chart">
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={chartData}>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="var(--admin-chart-grid)"
                    />
                    <XAxis dataKey="label" tick={CHART_TICK} />
                    <YAxis allowDecimals={false} tick={CHART_TICK} />
                    <Tooltip contentStyle={CHART_TOOLTIP_STYLE} />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="registrations"
                      name="Registrations"
                      stroke="#00b588"
                      strokeWidth={2}
                      dot={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="requests"
                      name="Requests"
                      stroke="#2563eb"
                      strokeWidth={2}
                      dot={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="emails"
                      name="Emails sent"
                      stroke="#9333ea"
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </section>

            <section
              className="metrics-chart-card"
              aria-label="Approved vs denied"
            >
              <h2>Decision outcomes</h2>
              <p className="metrics-chart-copy">
                Approved API keys issued versus denied requests for {rangeLabel}.
              </p>
              <div className="metrics-chart-wrap" data-testid="admin-metrics-outcomes-chart">
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart
                    data={[
                      {
                        label: "Approved",
                        count: summary.api_keys_approved,
                      },
                      { label: "Denied", count: summary.denied },
                    ]}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="var(--admin-chart-grid)"
                    />
                    <XAxis dataKey="label" tick={CHART_TICK} />
                    <YAxis allowDecimals={false} tick={CHART_TICK} />
                    <Tooltip contentStyle={CHART_TOOLTIP_STYLE} />
                    <Bar dataKey="count" name="Requests" radius={[6, 6, 0, 0]}>
                      <Cell fill="#00b588" />
                      <Cell fill="#dc2626" />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </section>
          </div>
        </>
      ) : null}
    </AdminShell>
  );
}
